import { useTranslation } from 'react-i18next'
import { CarbonateFormula, CARBONATES, UnitLabel } from '@/shared/config/config'
import {
  Button,
  SelectGroup,
  CheckboxGroup,
  NumericInputGroup,
  InputGroup
} from '@/common'
import calculateProcessEmissions from '@/features/reports/formulas/calculateProcessEmissions'
import { useCallback, useEffect, useMemo, useState } from 'react'

export type CarbonateDirectEmission = {
  emission: number
  activityData: string
  emissionFactor: string
  conversionFactor: string
  carbonate: string
  gypsumProducedTonnes: string
  isOutputBased: boolean
  materialName: string
}

const CALCIUM_CARBONATE = CARBONATES.find(c => c.formula === CarbonateFormula.Calcium)
const STOICHOMETRIC_VALUE_OF_DRY_GYPSUM = '0.2558' // CO2/t

export default function FlueGasCleaningEmissionForm({
  addEmission,
  initialValues,
  onCancel,
  onChange,
  showCarbonate,
  allowOutputBasedCalcium
}: {
  initialValues?: CarbonateDirectEmission
  addEmission: (payload: CarbonateDirectEmission) => void
  onCancel?: () => void
  onChange: (payload: CarbonateDirectEmission) => void
  showCarbonate: boolean
  allowOutputBasedCalcium: boolean
}) {
  const { t } = useTranslation()

  const hasPredefinedEmissionFactor = Boolean(initialValues?.carbonate)
  const selectedCarbonateEmissionFactor = useMemo(() => {
    return hasPredefinedEmissionFactor
      ? CARBONATES.find(c => c.formula === initialValues?.carbonate)?.emissionFactor
      : ''
  }, [initialValues?.carbonate, hasPredefinedEmissionFactor])

  const [
    {
      activityData,
      emissionFactor,
      conversionFactor,
      carbonate,
      gypsumProducedTonnes,
      isOutputBased,
      materialName
    },
    setValues
  ] = useState({
    activityData: initialValues?.activityData || '',
    emissionFactor:
      initialValues?.emissionFactor || selectedCarbonateEmissionFactor || (initialValues?.carbonate ? '' : '0'),
    conversionFactor: initialValues?.conversionFactor || (initialValues?.carbonate ? '1' : '0'),
    carbonate: initialValues?.carbonate || '',
    gypsumProducedTonnes: initialValues?.gypsumProducedTonnes || '',
    isOutputBased: false,
    materialName: initialValues?.materialName || ''
  })

  const resetForm = useCallback(() => {
    setValues({
      activityData: '',
      emissionFactor: '0',
      conversionFactor: '0',
      carbonate: '',
      gypsumProducedTonnes: '',
      isOutputBased: false,
      materialName: ''
    })
  }, [])

  const onValuesChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prev => ({
      ...prev,
      [evt.target.name]: evt.target.value
    }))
  }, [])

  const onCarbonateChange = useCallback((evt: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedCarbonate = CARBONATES.find(c => c.formula === evt.target.value)
    setValues(prev => ({
      ...prev,
      carbonate: selectedCarbonate?.formula || '',
      emissionFactor: selectedCarbonate ? selectedCarbonate.emissionFactor : '0',
      conversionFactor: selectedCarbonate ? '1' : '0'
    }))
  }, [])

  const onActivityDataChange = useCallback((value: string) => {
    setValues(prev => ({
      ...prev,
      activityData: value
    }))
  }, [])

  const toggleOutputBased = useCallback(() => {
    const itTurningOnOutputBased = !isOutputBased
    setValues(prev => ({
      ...prev,
      isOutputBased: itTurningOnOutputBased,
      gypsumProducedTonnes: itTurningOnOutputBased ? '' : prev.gypsumProducedTonnes,
      emissionFactor: itTurningOnOutputBased
        ? STOICHOMETRIC_VALUE_OF_DRY_GYPSUM
        : CALCIUM_CARBONATE?.emissionFactor || ''
    }))
  }, [selectedCarbonateEmissionFactor, isOutputBased])

  const emission = useMemo(() => {
    return calculateProcessEmissions({
      activityData: Number(activityData || gypsumProducedTonnes),
      emissionFactor: Number(emissionFactor),
      conversionFactor: Number(conversionFactor)
    })
  }, [activityData, gypsumProducedTonnes, emissionFactor, conversionFactor])

  const payload = useMemo(() => {
    return {
      emission,
      activityData,
      emissionFactor,
      conversionFactor,
      carbonate,
      gypsumProducedTonnes,
      isOutputBased,
      materialName
    }
  }, [
    activityData,
    emissionFactor,
    conversionFactor,
    carbonate,
    gypsumProducedTonnes,
    isOutputBased,
    emission,
    materialName
  ])

  const onSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
      addEmission(payload)
      resetForm()
    },
    [payload, addEmission, resetForm]
  )

  useEffect(() => {
    onChange(payload)
  }, [onChange, payload])

  const initialValuesHasCarbonate = Boolean(initialValues?.carbonate)
  const isCalciumCarbonate = carbonate === CarbonateFormula.Calcium

  return (
    <form onSubmit={onSubmit} className="space-y-2">
      {showCarbonate && (
        <SelectGroup
          name="carbonate"
          label={t(`report:common:fields.carbonate.label`)}
          value={carbonate}
          onChange={onCarbonateChange}
          options={[{ label: '-', value: '' }].concat(
            CARBONATES.map(carbonate => ({ label: carbonate.formula, value: carbonate.formula }))
          )}
        />
      )}
      {!initialValuesHasCarbonate && (
        <InputGroup
          name="materialName"
          label={t(`report:common:fields.materialName.label`)}
          value={materialName}
          onChange={onValuesChange}
        />
      )}
      {isCalciumCarbonate && allowOutputBasedCalcium && (
        <CheckboxGroup
          id="isOutputBased"
          label={t(`report:flueGasCleaningEmissionForm.fields.isOutputBased.label`)}
          checked={isOutputBased}
          onChange={toggleOutputBased}
        />
      )}
      {isOutputBased && allowOutputBasedCalcium ? (
        <NumericInputGroup
          name="gypsumProducedTonnes"
          label={t(`report:flueGasCleaningEmissionForm.fields.gypsumProducedTonnes.label`)}
          value={gypsumProducedTonnes}
          onChange={onValuesChange}
          suffix={UnitLabel.t}
          numericFormat={{
            decimalScale: 3
          }}
        />
      ) : (
        <NumericInputGroup
          name="activityData"
          label={t(`report:common:fields.activityData.label`)}
          value={activityData}
          numericFormat={{
            decimalScale: 3,
            allowNegative: false,
            thousandSeparator: ',',
            // Using the special `onValueChange` prop from the react-number-format library
            // to have access to the value without the thousand separators
            onValueChange: values => {
              onActivityDataChange(values.value)
            }
          }}
          suffix={UnitLabel.t}
        />
      )}
      <NumericInputGroup
        name="emissionFactor"
        label={t(`report:common:fields.emissionFactor.label`)}
        value={emissionFactor}
        onChange={onValuesChange}
        suffix={UnitLabel.tCO2PerT}
        numericFormat={{
          decimalScale: 3
        }}
      />

      <NumericInputGroup
        name="conversionFactor"
        label={t(`report:common:fields.conversionFactor.label`)}
        value={conversionFactor}
        onChange={onValuesChange}
        numericFormat={{
          decimalScale: 3,
          isAllowed: values => {
            const { floatValue } = values
            const maxValue = 1
            return floatValue === undefined || floatValue <= maxValue
          }
        }}
      />
      {!onChange && (
        <div className="flex justify-end space-x-2">
          {onCancel && (
            <Button sizeType="sm" variant="subtle" onClick={onCancel}>
              {t('common:actions.cancel')}
            </Button>
          )}
          <Button sizeType="sm" type="submit">
            {t('common:actions.add')}
          </Button>
        </div>
      )}
    </form>
  )
}
